import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  signInWithPopup, 
  signInWithRedirect,
  GoogleAuthProvider, 
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  onIdTokenChanged
} from 'firebase/auth';
import { auth } from '../config/firebase';
import apiService from '../services/api';

const AuthContext = createContext();

const getProfileCacheKey = (uid) => `hairrabbit_web_profile_${uid || 'guest'}`;
const PROFILE_REQUEST_TIMEOUT_MS = 12000;
const PROFILE_RETRY_ATTEMPTS = 2;
const PROFILE_RETRY_BASE_DELAY_MS = 600;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableNetworkError = (error) => {
  if (error?.response) return false;
  return (
    error?.code === 'ECONNABORTED' ||
    error?.code === 'ERR_NETWORK' ||
    /network|timeout/i.test(error?.message || '')
  );
};

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileResolved, setProfileResolved] = useState(false);
  const [idToken, setIdToken] = useState(null);

  // Sign up with email/password
  const signup = async (email, password) => {
    return createUserWithEmailAndPassword(auth, email, password);
  };

  // Sign in with email/password
  const login = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      return await signInWithPopup(auth, provider);
    } catch (error) {
      const code = error?.code || '';
      const shouldFallbackToRedirect = [
        'auth/popup-blocked',
        'auth/cancelled-popup-request',
        'auth/operation-not-supported-in-this-environment',
      ].includes(code);

      if (shouldFallbackToRedirect) {
        await signInWithRedirect(auth, provider);
        return null;
      }

      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    const uid = currentUser?.uid;
    if (uid) {
      localStorage.removeItem(getProfileCacheKey(uid));
    }
    setUserProfile(null);
    setIdToken(null);
    return firebaseSignOut(auth);
  };

  // Delete account data then sign out from Firebase
  const deleteAccount = async () => {
    await apiService.deleteAccount();
    await signOut();
  };

  // Fetch user profile from backend
  const fetchUserProfile = useCallback(async (token, options = {}) => {
    const allowOfflineFallback = options.allowOfflineFallback !== false;
    const skipLoadingState = options.skipLoadingState === true;
    if (!skipLoadingState) {
      setProfileLoading(true);
    }

    try {
      for (let attempt = 1; attempt <= PROFILE_RETRY_ATTEMPTS; attempt += 1) {
        try {
          const response = await apiService.client.get('/user/me', {
            headers: { Authorization: `Bearer ${token}` },
            timeout: PROFILE_REQUEST_TIMEOUT_MS,
          });
          setUserProfile(response.data);
          const effectiveUid = currentUser?.uid || auth.currentUser?.uid;
          if (effectiveUid) {
            localStorage.setItem(getProfileCacheKey(effectiveUid), JSON.stringify(response.data));
          }
          return response.data;
        } catch (error) {
          const status = error?.response?.status;

          // 404 = profile not created yet (new account); show onboarding bootstrap.
          // 401 means token rejected — do NOT auto-onboard; let onAuthStateChanged handle re-auth.
          if (status === 404) {
            const bootstrapProfile = {
              onboarding_completed: false,
              role: 'free',
              plan_type: 'free',
              trial_used: false,
              subscription_start: null,
              subscription_expiry: null,
              trial_active: false,
              trial_eligible: true,
              trial_days_left: 0,
              subscription_days_left: 0,
              subscription_status: 'inactive',
              current_plan: 'free',
              is_premium_active: false,
              is_paid_premium: false,
              subscription_trial_active: false,
            };
            setUserProfile(bootstrapProfile);
            return bootstrapProfile;
          }

          if (status === 401) {
            setUserProfile(null);
            return null;
          }

          const canRetry = attempt < PROFILE_RETRY_ATTEMPTS && isRetryableNetworkError(error);
          if (canRetry) {
            await wait(PROFILE_RETRY_BASE_DELAY_MS * attempt);
            continue;
          }

          if (allowOfflineFallback) {
            setUserProfile((prev) => ({
              ...prev,
              id: prev?.id || 'temp_user',
              email: prev?.email || auth.currentUser?.email || '',
              onboarding_completed: prev?.onboarding_completed !== false,
              role: prev?.role || 'free',
              plan_type: prev?.plan_type || 'free',
              trial_used: prev?.trial_used || false,
              subscription_start: prev?.subscription_start || null,
              subscription_expiry: prev?.subscription_expiry || null,
              trial_active: prev?.trial_active || false,
              trial_eligible: prev?.trial_eligible !== false,
              trial_days_left: prev?.trial_days_left || 0,
              subscription_days_left: prev?.subscription_days_left || 0,
              subscription_status: prev?.subscription_status || 'inactive',
              current_plan: prev?.current_plan || 'free',
              is_premium_active: prev?.is_premium_active || false,
              is_paid_premium: prev?.is_paid_premium || false,
              subscription_trial_active: prev?.subscription_trial_active || false,
              privacy_policy_accepted: prev?.privacy_policy_accepted !== false,
              privacy_consent_required: false,
              _offline_profile: true,
            }));
          }

          return null;
        }
      }
      return null;
    } finally {
      if (!skipLoadingState) {
        setProfileLoading(false);
      }
    }
  }, [currentUser]);

  const updateUserProfile = useCallback((patch) => {
    setUserProfile((prev) => {
      const next = { ...(prev || {}), ...(patch || {}) };
      const effectiveUid = currentUser?.uid || auth.currentUser?.uid;
      if (effectiveUid) {
        localStorage.setItem(getProfileCacheKey(effectiveUid), JSON.stringify(next));
      }
      return next;
    });
  }, [currentUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setProfileResolved(false);
      
      if (user) {
        try {
          const token = await user.getIdToken();
          setIdToken(token);
          apiService.setAuthToken(token);

          const cachedProfile = localStorage.getItem(getProfileCacheKey(user.uid));
          if (cachedProfile) {
            try {
              setUserProfile(JSON.parse(cachedProfile));
              setProfileResolved(true);
              fetchUserProfile(token, { allowOfflineFallback: true, skipLoadingState: true });
            } catch {
              await fetchUserProfile(token, { allowOfflineFallback: true });
            }
          } else {
            await fetchUserProfile(token, { allowOfflineFallback: true });
          }
        } catch (error) {
          console.error('Error getting token:', error);
        } finally {
          setProfileResolved(true);
        }
      } else {
        setIdToken(null);
        setUserProfile(null);
        setProfileResolved(true);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, [fetchUserProfile]);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setIdToken(null);
        apiService.setAuthToken(null);
        return;
      }

      try {
        const token = await user.getIdToken();
        setIdToken(token);
        apiService.setAuthToken(token);
      } catch (error) {
        console.error('Error refreshing token:', error);
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    let refreshTimer = null;
    if (currentUser) {
      // Proactively refresh token every 50 minutes
      refreshTimer = setInterval(async () => {
        try {
          const token = await currentUser.getIdToken(true);
          setIdToken(token);
          apiService.setAuthToken(token);
        } catch (error) {
          console.error('Error forcing token refresh:', error);
        }
      }, 50 * 60 * 1000);
    }

    return () => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
    };
  }, [currentUser]);

  const backendPremiumActive = userProfile?.is_premium_active === true;
  const backendPaidPremium = userProfile?.is_paid_premium === true;
  const backendTrialActive =
    userProfile?.trial_active === true ||
    userProfile?.current_plan === 'trial' ||
    userProfile?.subscription_trial_active === true;

  const hasActiveSubscriptionWindow =
    !!userProfile?.subscription_expiry && userProfile?.subscription_active !== false;

  const fallbackPaidPremium =
    userProfile?.role === 'premium' &&
    ['monthly', 'yearly'].includes(userProfile?.plan_type) &&
    hasActiveSubscriptionWindow &&
    !backendTrialActive;

  const isPaidPremium = backendPaidPremium || fallbackPaidPremium;
  const isInFreeTrial =
    backendTrialActive ||
    (userProfile?.plan_type === 'trial' && hasActiveSubscriptionWindow);
  const isPremium = backendPremiumActive || isPaidPremium || isInFreeTrial;

  const trialDaysLeft = userProfile?.trial_days_left || (isInFreeTrial ? userProfile?.subscription_days_left || 0 : 0);
  const subscriptionDaysLeft = userProfile?.subscription_days_left || 0;
  const currentPlan =
    userProfile?.current_plan ||
    (isInFreeTrial ? 'trial' : isPaidPremium ? userProfile?.plan_type || 'monthly' : 'free');
  const subscriptionStatus =
    userProfile?.subscription_status ||
    (isInFreeTrial ? 'trial' : isPaidPremium ? 'active' : 'inactive');
  const isTrialEligible = userProfile?.trial_eligible !== false && !isPremium;

  const value = {
    currentUser,
    userProfile,
    idToken,
    profileLoading,
    profileResolved,
    signup,
    login,
    signInWithGoogle,
    signOut,
    deleteAccount,
    isPremium,
    isPaidPremium,
    isInFreeTrial,
    trialDaysLeft,
    subscriptionDaysLeft,
    currentPlan,
    subscriptionStatus,
    isTrialEligible,
    fetchUserProfile,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
